import React, { useEffect, useState } from "react";
import {
Box,
Card,
Typography,
Button,
TextField,
Chip,
Checkbox,
FormControlLabel,
DialogTitle,
DialogContent,
DialogActions,
Snackbar,
Alert,
Pagination,
Stack
} from "@mui/material";

import { useSelector } from "react-redux";
import { Colors } from "../utils/colors";
import { adminFetch } from "../services/adminFetch";
import { formatAdminDateTime } from "../utils/adminDateTime";
import TableLoadingOverlay from "../components/TableLoadingOverlay";
import ResponsiveDialog from "../components/ResponsiveDialog";
import ResponsiveDataTable from "../components/ResponsiveDataTable";
import AdminPageContainer from "../components/AdminPageContainer";
import {
  adminPageSx,
  contentCardSx,
  dialogActionsResponsiveSx,
  mobileActionGroupSx,
  paginationBarSx
} from "../utils/adminResponsive";

const ticketFieldSx = {
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  background: "#fff",
  borderRadius: 1,
  "& .MuiInputBase-root": {
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box"
  }
};

export default function Tickets() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [notify, setNotify] = useState(true);
  const [password, setPassword] = useState("");
  const [reason, setReason] = useState("");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  const admin = useSelector((state) => state.auth.admin);
  const isSuperAdmin = admin === "super_admin";
  const rowsPerPage = 20;

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const loadTickets = async () => {
    setLoading(true);

    try {
      const data = await adminFetch("/api/admin/tickets", {
        method: "GET"
      });

      if (data?.status) {
        setTickets(data.tickets || []);
      }
    } catch (error) {
      console.log(error);
    }

    setLoading(false);
  };

  useEffect(() => {
    loadTickets();
  }, []);

  const filteredTickets = tickets.filter((ticket) => {
    const searchValue = search.toLowerCase();

    return (
      ticket.ticket_code?.toLowerCase().includes(searchValue) ||
      ticket.User?.email?.toLowerCase().includes(searchValue) ||
      ticket.Draw?.draw_code?.toLowerCase().includes(searchValue)
    );
  });

  useEffect(() => {
    setPage(1);
  }, [search]);

  const totalPages = Math.ceil(filteredTickets.length / rowsPerPage) || 1;
  const paginatedTickets = filteredTickets.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const openActionDialog = (ticket) => {
    setSelectedTicket(ticket);
    setNotify(true);
    setPassword("");
    setReason("");
    setOpen(true);
  };

  const handleToggleSuspension = async () => {
    if (!selectedTicket) return;

    try {
      const endpoint = selectedTicket.is_suspended
        ? `/api/admin/tickets/${selectedTicket.id}/unsuspend`
        : `/api/admin/tickets/${selectedTicket.id}/suspend`;

      const data = await adminFetch(endpoint, {
        method: "PUT",
        body: JSON.stringify({
          notify,
          password,
          reason
        })
      });

      if (!data?.status) {
        showSnackbar(data?.message || "Operation failed", "error");
        return;
      }

      showSnackbar(
        selectedTicket.is_suspended
          ? "Ticket unsuspended successfully"
          : "Ticket suspended successfully"
      );

      setOpen(false);
      setSelectedTicket(null);
      await loadTickets();
    } catch (error) {
      console.log(error);
      showSnackbar("Server error", "error");
    }
  };

  const getTicketStatusChip = (ticket) => {
    if (ticket.is_suspended) {
      return <Chip label="Suspended" sx={{ bgcolor: "#d32f2f", color: "#fff" }} />;
    }

    switch (ticket.status) {
      case "won":
        return <Chip label="Won" sx={{ bgcolor: "#2e7d32", color: "#fff" }} />;
      case "lost":
        return <Chip label="Lost" sx={{ bgcolor: "#6d4c41", color: "#fff" }} />;
      default:
        return <Chip label="Active" sx={{ bgcolor: "#1976d2", color: "#fff" }} />;
    }
  };

  const ticketColumns = [
    {
      key: "index",
      label: "S/N",
      render: (_ticket, index) => (page - 1) * rowsPerPage + index + 1,
      hideOnMobile: true
    },
    {
      key: "ticket_code",
      label: "Ticket Code",
      render: (ticket) => ticket.ticket_code
    },
    {
      key: "user",
      label: "User",
      render: (ticket) => `${ticket.User?.first_name || ""} ${ticket.User?.last_name || ""}`.trim() || "-",
      hideOnMobile: true
    },
    {
      key: "email",
      label: "Email",
      render: (ticket) => ticket.User?.email || "-"
    },
    {
      key: "draw",
      label: "Draw",
      render: (ticket) => ticket.Draw?.draw_code || "-"
    },
    {
      key: "amount",
      label: "Amount",
      render: (ticket) => `₦${Number(ticket.purchase_amount || 0).toLocaleString()}`
    },
    {
      key: "funding_source",
      label: "Funding Source",
      render: (ticket) => ticket.funding_source || "Others"
    },
    {
      key: "status",
      label: "Status",
      render: (ticket) => getTicketStatusChip(ticket)
    },
    {
      key: "created_at",
      label: "Date",
      render: (ticket) => formatAdminDateTime(ticket.created_at)
    },
    {
      key: "actions",
      label: "Action",
      isActions: true,
      mobileValueSx: mobileActionGroupSx,
      render: (ticket) => (
        <Button
          size="small"
          variant="contained"
          color={ticket.is_suspended ? "success" : "warning"}
          disabled={!isSuperAdmin}
          onClick={() => openActionDialog(ticket)}
        >
          {ticket.is_suspended ? "Unsuspend" : "Suspend"}
        </Button>
      )
    }
  ];

  return (
    <Box
      sx={{
        ...adminPageSx
      }}
    >
      <AdminPageContainer>
        <Typography variant="h5" fontWeight={700} sx={{ color: Colors.accent, mb: 3 }}>
          Tickets Management
        </Typography>

        <Card sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3, ...contentCardSx }}>
          <TextField
            placeholder="Search ticket code, email or draw code"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            fullWidth
            sx={{ ...ticketFieldSx, mb: 2, maxWidth: { xs: "100%", md: 420 } }}
          />

          <Box sx={{ position: "relative", minHeight: 320, width: "100%", maxWidth: "100%", minWidth: 0 }}>
            <ResponsiveDataTable
              columns={ticketColumns}
              rows={paginatedTickets}
              loading={loading}
              minWidth={980}
              emptyMinHeight={180}
              emptyMessage="No tickets found"
              mobileTitle={(ticket) => ticket.ticket_code}
            />

            <TableLoadingOverlay loading={loading} />
          </Box>

          {!loading && filteredTickets.length > rowsPerPage ? (
            <Stack sx={paginationBarSx}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Page {page} of {totalPages}
              </Typography>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_event, value) => setPage(value)}
                color="primary"
                shape="rounded"
                showFirstButton
                showLastButton
                sx={{
                  "& .MuiPaginationItem-root": { color: "text.primary" },
                  "& .Mui-selected": {
                    bgcolor: `${Colors.accent} !important`,
                    color: "#003300 !important"
                  }
                }}
              />
            </Stack>
          ) : null}
        </Card>
      </AdminPageContainer>

      <ResponsiveDialog open={open} onClose={() => setOpen(false)} maxWidth="xs">
        <DialogTitle>
          {selectedTicket?.is_suspended ? "Unsuspend Ticket" : "Suspend Ticket"}
        </DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ mb: 2 }}>
            {selectedTicket?.is_suspended
              ? "Do you want to restore this ticket?"
              : "Do you want to suspend this ticket?"}
          </Typography>

          {!selectedTicket?.is_suspended && (
            <>
            <TextField label="Admin Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} fullWidth autoComplete="current-password" sx={{ mb: 2 }} />
            <TextField label="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} fullWidth multiline minRows={2} sx={{ mb: 1 }} />
            <FormControlLabel
              control={
                <Checkbox
                  checked={notify}
                  onChange={(e) => setNotify(e.target.checked)}
                />
              }
              label={`Notify user (${selectedTicket?.User?.email || ""})`}
            />
            </>
          )}
        </DialogContent>
        <DialogActions sx={dialogActionsResponsiveSx}>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleToggleSuspension} disabled={!selectedTicket?.is_suspended && !password}>
            Confirm
          </Button>
        </DialogActions>
      </ResponsiveDialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} onClose={handleCloseSnackbar}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
